import { RefObject, useEffect, useMemo, useRef } from 'react';

import { EncodedPacketSink, InputVideoTrack } from 'mediabunny';

import { FrameCodec } from '../utils/FrameCodec';

import { useRequestAnimationFrame } from './useRequestAnimationFrame';
import { VideoMetadata } from './useVideoCache';

const TARGET_FPS = 25;
const DECODE_INTERVAL_MS = 1000 / TARGET_FPS;

type DecodeAndDrawArgs = {
    canvas: HTMLCanvasElement;
    codec: FrameCodec;
    duration: number;
    frameCount: number;
    requestedFrame: number;
    sink: EncodedPacketSink;
};

type UseVideoFramesArgs = {
    src: string;
    track: InputVideoTrack;
    metadata: VideoMetadata;
};

function frameToSeconds(frame: number, duration: number, frameCount: number) {
    return (frame * duration) / frameCount;
}

function drawFrame(canvas: HTMLCanvasElement, frame: VideoFrame) {
    const context = canvas.getContext('2d');

    if (!context) {
        return;
    }

    const width = frame.displayWidth || frame.codedWidth;
    const height = frame.displayHeight || frame.codedHeight;

    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }

    context.drawImage(frame, 0, 0, canvas.width, canvas.height);
}

async function decodeAndDraw({ canvas, codec, duration, frameCount, requestedFrame, sink }: DecodeAndDrawArgs) {
    const lookupSeconds = frameToSeconds(requestedFrame, duration, frameCount);
    const packet = await sink.getPacket(lookupSeconds, {
        skipLiveWait: true,
    });

    if (!packet) {
        return;
    }

    const chunk = packet.toEncodedVideoChunk();
    const decodedFrame = await codec.decode(chunk);

    try {
        drawFrame(canvas, decodedFrame);
    } finally {
        decodedFrame.close();
    }
}

export const useVideoFrames = (
    ref: RefObject<HTMLCanvasElement | null>,
    frame: number,
    frameCount: number,
    { track, metadata }: UseVideoFramesArgs,
) => {
    const latestFrameRef = useRef(frame);
    latestFrameRef.current = frame;
    const isDecodingRef = useRef(false);
    const lastDecodeAtRef = useRef(0);

    const sink = useMemo(() => new EncodedPacketSink(track), [track]);
    const codec = useMemo(() => new FrameCodec(metadata.decoderConfig), [metadata.decoderConfig]);

    useEffect(() => {
        return () => {
            codec.close();
        };
    }, [codec]);

    useRequestAnimationFrame(() => {
        const canvas = ref.current;

        if (!canvas || frameCount <= 0 || isDecodingRef.current) {
            return;
        }

        const now = performance.now();

        if (now - lastDecodeAtRef.current < DECODE_INTERVAL_MS) {
            return;
        }

        lastDecodeAtRef.current = now;
        isDecodingRef.current = true;

        decodeAndDraw({
            canvas,
            codec,
            duration: metadata.duration,
            frameCount,
            requestedFrame: latestFrameRef.current,
            sink,
        })
            .catch((error) => {
                console.error('[video-frames] decode failed', error);
            })
            .finally(() => {
                isDecodingRef.current = false;
            });
    });
};
